// hero-greeting — dynamische Markt-Hero-Begrüßung im Business-Modus (alle User).
//
// Analog zur /privat-Begrüßung (Textbausteine nach Kontext), aber ohne die
// Doc-internen Sprüche. Nutzt Tageszeit + Wochentag/Datum + letzten Besuch.
// `now` ist injizierbar für deterministische Tests.

import {
  berlinDateKey,
  berlinHour,
  formatBerlinRelative,
  formatBerlinTime,
  formatBerlinWeekdayDate,
  formatGap,
} from './berlin-time';
import type { LastSeenResponse } from './last-seen-api';

export interface HeroGreeting {
  /** Tageszeit-Anrede OHNE Name („Guten Morgen") — der Hero setzt den Namen im
   *  Gold-Verlauf selbst dahinter. */
  daypart: string;
  /** Kontext-Zeile in EVE-Stimme (letzter Besuch + Datum + Anstoß). */
  subline: string;
}

function daypartFor(hour: number): string {
  if (hour >= 5 && hour < 11) return 'Guten Morgen';
  if (hour >= 18 || hour < 5) return 'Guten Abend';
  return 'Guten Tag';
}

export function buildHeroGreeting(
  lastSeen: LastSeenResponse | null,
  _name: string,
  now: Date = new Date(),
): HeroGreeting {
  const hour = berlinHour(now);
  const isMorning = hour >= 5 && hour < 11;
  const isEvening = hour >= 18 || hour < 5;
  const daypart = daypartFor(hour);
  const weekdayDate = formatBerlinWeekdayDate(now);

  const rawPrev = lastSeen?.prev_at ? new Date(lastSeen.prev_at) : null;
  const prev = rawPrev && Number.isFinite(rawPrev.getTime()) ? rawPrev : null;

  // Erstbesuch (kein vorheriger Zeitstempel).
  if (!prev) {
    const opener = isMorning
      ? 'Session läuft, ich hab ein Auge drauf.'
      : isEvening
        ? 'Der Tag ist fast rum — ich schau noch auf den Markt.'
        : 'Ich behalte den Markt im Blick.';
    return {
      daypart,
      subline: `${weekdayDate}. ${opener} Zeig mir einen Chart oder frag, was gerade zählt.`,
    };
  }

  const prevTime = formatBerlinTime(prev);
  const sameDay = berlinDateKey(prev) === berlinDateKey(now);
  const gapH = (now.getTime() - prev.getTime()) / 3_600_000;

  // Sehr kurze Unterbrechung am selben Tag — kein „seit X weg", nur weitermachen.
  if (sameDay && gapH < 1) {
    return {
      daypart,
      subline: `${weekdayDate}. Ich bin noch dran. Was schauen wir uns an?`,
    };
  }

  if (sameDay) {
    return {
      daypart,
      subline: `Seit ${prevTime} warst du weg — hat sich einiges bewegt. ${weekdayDate}. Was schauen wir uns an?`,
    };
  }

  // Anderer Tag: letzter Besuch relativ + Lücke.
  const rel = formatBerlinRelative(prev, now);
  return {
    daypart,
    subline: `Zuletzt ${rel} um ${prevTime} — ${formatGap(gapH)} her. ${weekdayDate}. Willst du die Lage kurz, oder hast du was Konkretes?`,
  };
}
